import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import ImageField from "./ImageField";

const labelCls = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-rose";

function Field({ label, value, onChange, textarea, type = "text", testid }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {textarea ? (
        <textarea data-testid={testid} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-none`} />
      ) : (
        <input data-testid={testid} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

export default function BrandSettings({ settings, onSaved }) {
  const [brand, setBrand] = useState({ ...(settings?.brand || {}) });
  const [contact, setContact] = useState({ ...(settings?.contact || {}) });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await api.put("/admin/settings", { brand, contact });
      setSaved(true);
      onSaved?.();
    } catch {
      setError("Could not save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const b = (k, v) => setBrand((f) => ({ ...f, [k]: v }));
  const c = (k, v) => setContact((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="brand-settings" className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-3xl border border-line bg-white p-6 md:p-8">
        <h3 className="mb-6 font-serif text-2xl font-medium">Brand</h3>
        <div className="flex flex-col gap-5">
          <Field label="Website Name" value={brand.name} onChange={(v) => b("name", v)} testid="brand-name-input" />
          <Field label="Tagline" value={brand.tagline} onChange={(v) => b("tagline", v)} testid="brand-tagline-input" />
          <Field label="Brand Description (footer)" value={brand.description} onChange={(v) => b("description", v)} textarea testid="brand-description-input" />
          <Field label="Footer Text (bottom bar)" value={brand.footer_text} onChange={(v) => b("footer_text", v)} testid="brand-footer-text-input" />
          <Field label="Header Button Label" value={brand.cta_label} onChange={(v) => b("cta_label", v)} testid="brand-cta-input" />
          <ImageField label="Logo (optional — replaces the heart icon)" value={brand.logo} onChange={(v) => b("logo", v)} testid="brand-logo" />
          <ImageField label="Favicon (browser tab icon)" value={brand.favicon} onChange={(v) => b("favicon", v)} testid="brand-favicon" />
        </div>
      </div>

      <div className="rounded-3xl border border-line bg-white p-6 md:p-8">
        <h3 className="mb-6 font-serif text-2xl font-medium">Contact & Social</h3>
        <div className="flex flex-col gap-5">
          <Field label="WhatsApp Number (with country code, digits only)" value={contact.whatsapp} onChange={(v) => c("whatsapp", v.replace(/[^\d]/g, ""))} testid="contact-whatsapp-input" />
          <Field label="Display Phone Number" value={contact.phone_display} onChange={(v) => c("phone_display", v)} testid="contact-phone-input" />
          <Field label="Default WhatsApp Message" value={contact.whatsapp_message} onChange={(v) => c("whatsapp_message", v)} testid="contact-wa-message-input" />
          <Field label="Email" value={contact.email} onChange={(v) => c("email", v)} testid="contact-email-input" />
          <Field label="Address" value={contact.address} onChange={(v) => c("address", v)} textarea testid="contact-address-input" />
          <Field label="Instagram Handle" value={contact.instagram} onChange={(v) => c("instagram", v)} testid="contact-instagram-input" />
          <Field label="Instagram URL" value={contact.instagram_url} onChange={(v) => c("instagram_url", v)} testid="contact-instagram-url-input" />
          <Field label="Facebook Name" value={contact.facebook} onChange={(v) => c("facebook", v)} testid="contact-facebook-input" />
          <Field label="Facebook URL" value={contact.facebook_url} onChange={(v) => c("facebook_url", v)} testid="contact-facebook-url-input" />
          <Field label="Payment Note (e.g. No COD)" value={contact.payment_note} onChange={(v) => c("payment_note", v)} testid="contact-payment-note-input" />
          <label className="flex cursor-pointer items-center gap-3 text-sm font-medium">
            <input
              data-testid="contact-floating-toggle"
              type="checkbox"
              checked={contact.floating_whatsapp !== false}
              onChange={(e) => c("floating_whatsapp", e.target.checked)}
              className="h-4 w-4 accent-[#C48B81]"
            />
            Show floating WhatsApp button on the website
          </label>
        </div>
      </div>

      <div className="lg:col-span-2">
        {error && <p data-testid="brand-save-error" className="mb-3 rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600">{error}</p>}
        {saved && (
          <p data-testid="brand-save-success" className="mb-3 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-700">
            <CheckCircle2 size={15} /> Saved — the website now uses these details.
          </p>
        )}
        <button
          data-testid="brand-save-button"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white disabled:opacity-60"
        >
          {saving ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : "Save Brand & Contact Settings"}
        </button>
      </div>
    </div>
  );
}
